import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import BookSearchPreview from './BookSearchPreview'
import { useBookSearch, BookSearchResult } from '../hooks/useBookSearch'
import Modal from './Modal'

interface ProposeBooksProps {
  clubId: number
  onClose: () => void
  onSuccess: () => void
}

export default function ProposeBooks({ clubId, onClose, onSuccess }: ProposeBooksProps) {
  const { searchQuery, setSearchQuery, results, loading, error: searchError } = useBookSearch()
  const [selectedBooks, setSelectedBooks] = useState<BookSearchResult[]>([])
  const [proposing, setProposing] = useState(false)
  const [error, setError] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [votingDeadline, setVotingDeadline] = useState('')
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [previewBook, setPreviewBook] = useState<BookSearchResult | null>(null)

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [results])

  useEffect(() => {
    if (searchError) {
      setError(searchError)
    }
  }, [searchError])

  const handleAddBook = (book: BookSearchResult) => {
    if (selectedBooks.length >= 5) {
      setError('You can only propose up to 5 books')
      return
    }

    // Check if book already selected
    const alreadySelected = selectedBooks.some(b =>
      b.title === book.title && b.author === book.author
    )

    if (alreadySelected) {
      setError('This book is already selected')
      return
    }

    setSelectedBooks([...selectedBooks, book])
    setSearchQuery('')
    setResults([])
    setError('')
  }

  const handleRemoveBook = (index: number) => {
    setSelectedBooks(selectedBooks.filter((_, i) => i !== index))
  }

  const handleProposeBooks = async () => {
    if (selectedBooks.length < 2) {
      setError('Please select at least 2 books')
      return
    }

    if (!votingDeadline) {
      setError('Please set a voting deadline')
      return
    }

    setProposing(true)
    setError('')

    try {
      const response = await fetchWithCsrf(`/api/v1/clubs/${clubId}/book_proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          book_proposal: {
            voting_deadline: votingDeadline
          },
          books: selectedBooks.map(book => ({
            title: book.title,
            author: book.author,
            isbn: book.isbn,
            description: book.description,
            cover_url: book.cover_url
          }))
        })
      })

      if (response.ok) {
        onSuccess()
        onClose()
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to propose books')
      }
    } catch (err) {
      setError('Failed to propose books')
    } finally {
      setProposing(false)
    }
  }

  return (
    <>
      <Modal isOpen={true} onClose={onClose} size="xl" showCloseButton={true} className="overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Propose Books for Voting</h2>
              <p className="text-gray-600">Select 2-5 books for members to vote on</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {showConfirmation ? (
            /* Confirmation Step */
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Selected Books ({selectedBooks.length})</h3>
                <div className="space-y-3">
                  {selectedBooks.map((book, index) => (
                    <div key={index} className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                      {book.cover_url ? (
                        <img
                          src={book.cover_url}
                          alt={book.title}
                          className="w-12 h-18 object-cover rounded shadow-sm"
                        />
                      ) : (
                        <div className="w-12 h-18 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate">{book.title}</h4>
                        <p className="text-sm text-gray-600">by {book.author}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="voting-deadline" className="block text-sm font-medium text-gray-700 mb-2">
                  Voting Deadline *
                </label>
                <input
                  id="voting-deadline"
                  type="date"
                  value={votingDeadline}
                  onChange={(e) => setVotingDeadline(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  autoFocus
                />
                <p className="mt-2 text-xs text-gray-500">
                  Members will vote by this date. After the deadline, you'll select the winner.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowConfirmation(false)
                    setVotingDeadline('')
                    setError('')
                  }}
                  className="flex-1 px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all border-2 border-gray-300"
                >
                  Back
                </button>
                <button
                  onClick={handleProposeBooks}
                  disabled={proposing || !votingDeadline}
                  className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-3 px-6 rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30"
                >
                  {proposing ? 'Proposing...' : 'Start Voting'}
                </button>
              </div>
            </div>
          ) : (
            /* Book Selection Step */
            <>
              {/* Selected Books */}
              {selectedBooks.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-gray-900">
                      Selected Books ({selectedBooks.length}/5)
                    </h3>
                    {selectedBooks.length >= 2 && (
                      <button
                        onClick={() => setShowConfirmation(true)}
                        className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Continue →
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {selectedBooks.map((book, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-sm truncate">{book.title}</h4>
                          <p className="text-xs text-gray-600">by {book.author}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveBook(index)}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by title or author..."
                    className="w-full px-4 py-3 pl-12 border-2 border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                    autoFocus={selectedBooks.length === 0}
                  />
                  <svg className="absolute left-4 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                {loading && (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="text-gray-600 mt-2">Searching...</p>
                  </div>
                )}

                {!loading && results.length > 0 && (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {results.map((book, index) => (
                      <button
                        key={index}
                        onClick={() => setPreviewBook(book)}
                        className={`w-full bg-white border-2 rounded-xl p-4 transition-all text-left ${
                          highlightedIndex === index
                            ? 'border-indigo-500 shadow-md bg-indigo-50'
                            : 'border-gray-200 hover:border-indigo-400 hover:shadow-md'
                        }`}
                      >
                        <div className="flex flex-col sm:flex-row gap-4">
                          {book.cover_url ? (
                            <img
                              src={book.cover_url}
                              alt={book.title}
                              className="w-16 h-24 object-cover rounded shadow-sm"
                            />
                          ) : (
                            <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center">
                              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                              </svg>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 mb-1">{book.title}</h3>
                            <p className="text-sm text-gray-600 mb-2">by {book.author}</p>
                            <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                              {book.first_publish_year && <span>{book.first_publish_year}</span>}
                              {book.number_of_pages && (
                                <>
                                  {book.first_publish_year && <span>•</span>}
                                  <span>{book.number_of_pages} pages</span>
                                </>
                              )}
                            </div>
                            <p className="text-xs text-indigo-600 mt-1">Click to see details</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!loading && searchQuery.length >= 2 && results.length === 0 && (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-gray-600">No books found</p>
                    <p className="text-sm text-gray-500 mt-1">Try a different search term</p>
                  </div>
                )}

                {searchQuery.length < 2 && results.length === 0 && selectedBooks.length === 0 && (
                  <div className="text-center py-12">
                    <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <p className="text-gray-600">Start typing to search for books</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Book Preview Modal */}
      {previewBook && (
        <BookSearchPreview
          book={previewBook}
          onClose={() => setPreviewBook(null)}
          onSelect={() => {
            handleAddBook(previewBook)
            setPreviewBook(null)
          }}
          showSelectButton={true}
        />
      )}
    </>
  )
}
